import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import {
  LogEntry,
  LogLevel,
  LOG_LEVELS,
  LOG_FILTER_KEYS,
  LEVEL_BAR_COLOR,
  logColumns,
  nodesToApiFilters,
  normalizeLevel,
  levelCountsFromHistogram,
  LogsHistogramBucket,
} from "./LogsData";
import LogEntryModal from "./LogEntryModal";
import LogsFilterBuilder, { FilterNode } from "./LogsFilterBuilder";
import LogsTimeRangePicker, {
  DEFAULT_PRESET,
  resolvePreset,
  TimeRange,
  TimeRangeMode,
} from "./LogsTimeRangePicker";
import { searchLogs, ApiLog } from "./request";
import { getTableBodyMaxHeight } from "../../helpers/tableViewport";

const PAGE_SIZE = 500;
const FETCH_DEBOUNCE_MS = 350;

interface StatCardProps {
  label: string;
  value: number;
  tone: "neutral" | "error" | "warning" | "success";
}

const toneClasses: Record<StatCardProps["tone"], string> = {
  neutral:
    "bg-semantic-bg-primary border-semantic-border-primary text-semantic-text-primary",
  error:
    "bg-semantic-bg-danger-secondary border-semantic-border-primary text-semantic-text-danger",
  warning:
    "bg-semantic-bg-warning-secondary border-semantic-border-primary text-semantic-text-warning",
  success:
    "bg-semantic-bg-success-secondary border-semantic-border-primary text-semantic-text-success",
};

const StatCard: React.FC<StatCardProps> = ({ label, value, tone }) => (
  <div
    className={`rounded-xl border px-4 py-3 flex flex-col gap-1 ${toneClasses[tone]}`}
  >
    <span className="text-xs uppercase tracking-wide opacity-80">{label}</span>
    <span className="text-2xl font-semibold font-quicksand">
      {value.toLocaleString()}
    </span>
  </div>
);

interface HistogramProps {
  buckets: LogsHistogramBucket[] | null;
  range: { start: Date; end: Date };
  onBucketClick?: (start: Date, end: Date) => void;
}

const LogsHistogram: React.FC<HistogramProps> = ({
  buckets,
  range,
  onBucketClick,
}) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const { normalized, maxCount, tickLabels, bucketMs } = useMemo(() => {
    const sMs = range.start.getTime();
    const eMs = range.end.getTime();
    const span = Math.max(eMs - sMs, 1);

    const rows =
      buckets?.map((b) => {
        const counts: Record<LogLevel, number> = {
          ERROR: Number(b.ERROR || 0),
          WARN: Number(b.WARN || 0),
          INFO: Number(b.INFO || 0),
          DEBUG: Number(b.DEBUG || 0),
          TRACE: Number(b.TRACE || 0),
        };
        return {
          start: new Date(b.bucket),
          counts,
          total: Number(b.total || 0),
        };
      }) || [];

    const max = rows.reduce((m, r) => (r.total > m ? r.total : m), 0);

    // Derive bucket width from the first two buckets or the full span.
    const bMs =
      rows.length > 1
        ? rows[1].start.getTime() - rows[0].start.getTime()
        : span;

    const ticks = [0, 0.25, 0.5, 0.75, 1].map((frac) =>
      format(new Date(sMs + span * frac), "HH:mm")
    );

    return {
      normalized: rows,
      maxCount: max,
      tickLabels: ticks,
      bucketMs: bMs,
    };
  }, [buckets, range]);

  const hovered = hoveredIdx !== null ? normalized[hoveredIdx] : null;
  const hoveredRange = hovered
    ? {
        start: hovered.start,
        end: new Date(hovered.start.getTime() + bucketMs),
      }
    : null;

  const leftPct =
    hoveredIdx !== null && normalized.length > 0
      ? ((hoveredIdx + 0.5) / normalized.length) * 100
      : 0;
  const tooltipAnchor =
    leftPct < 12
      ? "left-0 translate-x-0"
      : leftPct > 88
        ? "right-0 left-auto translate-x-0"
        : "-translate-x-1/2";
  const tooltipLeftStyle =
    leftPct < 12 || leftPct > 88 ? undefined : `${leftPct}%`;

  if (normalized.length === 0) {
    return (
      <div className="h-32 flex items-center justify-center text-xs text-semantic-text-secondary">
        No activity in the selected range.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <div className="flex items-end gap-[2px] h-32 px-1">
          {normalized.map((b, i) => {
            const heightPct = maxCount === 0 ? 0 : (b.total / maxCount) * 100;
            const isHovered = hoveredIdx === i;
            const clickable = Boolean(onBucketClick) && b.total > 0;
            return (
              <div
                key={i}
                className={`flex-1 flex flex-col justify-end min-w-[4px] rounded-t-sm overflow-hidden transition-all ${
                  isHovered
                    ? "opacity-100 outline outline-2 outline-semantic-border-brand-secondary"
                    : "opacity-85 hover:opacity-100"
                } ${clickable ? "cursor-pointer" : ""}`}
                style={{ height: `${Math.max(heightPct, b.total > 0 ? 2 : 0)}%` }}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() =>
                  setHoveredIdx((prev) => (prev === i ? null : prev))
                }
                onClick={() => {
                  if (clickable && onBucketClick) {
                    onBucketClick(
                      b.start,
                      new Date(b.start.getTime() + bucketMs)
                    );
                  }
                }}
              >
                {LOG_LEVELS.map((lvl) => {
                  const count = b.counts[lvl];
                  if (count === 0) return null;
                  const segPct = b.total === 0 ? 0 : (count / b.total) * 100;
                  return (
                    <div
                      key={lvl}
                      style={{
                        height: `${segPct}%`,
                        background: LEVEL_BAR_COLOR[lvl],
                      }}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>

        {hoveredIdx !== null && hovered && hoveredRange && (
          <div
            className={`absolute bottom-full mb-2 z-10 pointer-events-none ${tooltipAnchor}`}
            style={tooltipLeftStyle ? { left: tooltipLeftStyle } : undefined}
          >
            {/* Inverse surface + inverse text so both flip together — a literal
                `text-white` here was unreadable in dark mode (CTX-243). */}
            <div className="bg-semantic-bg-inverse-secondary text-semantic-text-inverse-primary rounded-md shadow-lg px-3 py-2 text-xs min-w-[200px] border border-semantic-border-inverse-secondary">
              <div className="font-mono text-[10px] opacity-80 mb-1">
                {format(hoveredRange.start, "HH:mm:ss")} →{" "}
                {format(hoveredRange.end, "HH:mm:ss")}
              </div>
              <div className="font-semibold mb-1.5">
                {hovered.total.toLocaleString()} event
                {hovered.total === 1 ? "" : "s"}
              </div>
              {hovered.total === 0 ? (
                <div className="text-[11px] opacity-70">No events</div>
              ) : (
                LOG_LEVELS.map((lvl) =>
                  hovered.counts[lvl] > 0 ? (
                    <div
                      key={lvl}
                      className="flex items-center gap-1.5 leading-snug"
                    >
                      <span
                        className="w-2 h-2 rounded-sm inline-block flex-shrink-0"
                        style={{ background: LEVEL_BAR_COLOR[lvl] }}
                      />
                      <span className="flex-1">{lvl}</span>
                      <span className="font-mono">{hovered.counts[lvl]}</span>
                    </div>
                  ) : null
                )
              )}
              {onBucketClick && hovered.total > 0 && (
                <div className="mt-1.5 pt-1.5 border-t border-white/20 text-[10px] opacity-70">
                  Click to zoom into this range
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between px-1 text-[10px] font-mono text-semantic-text-secondary">
        {tickLabels.map((t, i) => (
          <span key={i}>{t}</span>
        ))}
      </div>
      <div className="flex gap-4 pt-1">
        {LOG_LEVELS.map((lvl) => (
          <div key={lvl} className="flex items-center gap-1.5 text-xs">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm"
              style={{ background: LEVEL_BAR_COLOR[lvl] }}
            />
            <span className="text-semantic-text-secondary">{lvl}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const initialRange = (): TimeRange => resolvePreset(DEFAULT_PRESET);
const initialMode = (): TimeRangeMode => ({ kind: "preset", key: DEFAULT_PRESET });

function apiToEntry(l: ApiLog): LogEntry {
  return {
    id: l.id,
    timestamp: l.timestamp,
    level: normalizeLevel(l.level),
    reflectorId: l.reflectorId || "",
    reflectorName: l.reflectorName || "",
    reflectorUuid: l.reflectorUuid || "",
    source: l.source || "",
    message: l.message || "",
  };
}

const LogsPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);

  const [range, setRange] = useState<TimeRange>(initialRange);
  const [rangeMode, setRangeMode] = useState<TimeRangeMode>(initialMode);
  const [filterNodes, setFilterNodes] = useState<FilterNode[]>([]);

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<LogEntry | null>(null);
  const [histogram, setHistogram] = useState<LogsHistogramBucket[] | null>(null);
  const [total, setTotal] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  const histogramRange = range;

  const loadLogs = useCallback(async () => {
    if (!tenantId) {
      setLogs([]);
      setHistogram(null);
      setTotal(0);
      setFetchError(null);
      return;
    }
    const seq = ++requestSeq.current;
    try {
      setIsFetching(true);
      setFetchError(null);
      const token = await getAccessTokenSilently();
      const resp = await searchLogs(tenantId, token, {
        timeRange: {
          from: histogramRange.start.toISOString(),
          to: histogramRange.end.toISOString(),
        },
        filters: nodesToApiFilters(filterNodes),
        page: { size: PAGE_SIZE },
      });
      if (seq !== requestSeq.current) return; // stale response
      const data = resp.data;
      setLogs(Array.isArray(data?.logs) ? data.logs.map(apiToEntry) : []);
      setHistogram(Array.isArray(data?.histogram) ? data.histogram : null);
      setTotal(Number(data?.total || 0));
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      const resp = (
        err as {
          response?: {
            data?: { description?: string; detail?: unknown };
          };
        }
      )?.response?.data;
      const detail =
        resp?.detail !== undefined
          ? typeof resp.detail === "string"
            ? resp.detail
            : JSON.stringify(resp.detail)
          : null;
      const base =
        resp?.description ||
        (err as { message?: string })?.message ||
        "Failed to load logs.";
      setFetchError(detail ? `${base} — ${detail}` : base);
      setLogs([]);
      setHistogram(null);
      setTotal(0);
    } finally {
      if (seq === requestSeq.current) setIsFetching(false);
    }
  }, [
    tenantId,
    getAccessTokenSilently,
    histogramRange.start,
    histogramRange.end,
    filterNodes,
  ]);

  // Debounce filter / range changes so the user can type without hammering
  // the API. Range picker emits a single event, but filter chips stream edits.
  useEffect(() => {
    const t = setTimeout(() => {
      loadLogs();
    }, FETCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [loadLogs]);

  const counts = useMemo(() => levelCountsFromHistogram(histogram), [histogram]);

  const refreshRange = useCallback(() => {
    if (rangeMode.kind === "preset") {
      setRange(resolvePreset(rangeMode.key));
    } else {
      loadLogs();
    }
  }, [rangeMode, loadLogs]);

  const handleRangeChange = (next: TimeRange, mode: TimeRangeMode) => {
    setRange(next);
    setRangeMode(mode);
  };

  const clearFilters = () => {
    setFilterNodes([]);
    setRange(resolvePreset(DEFAULT_PRESET));
    setRangeMode({ kind: "preset", key: DEFAULT_PRESET });
  };

  const hasActiveFilter = filterNodes.length > 0;

  return (
    <div className="relative space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3 px-1">
        <div>
          <h1 className="font-quicksand text-2xl font-semibold text-semantic-text-primary">
            Logs
          </h1>
          <p className="text-sm text-semantic-text-secondary">
            Real-time events across your CTX Reflectors v2.0
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isFetching && (
            <span className="text-xs text-semantic-text-secondary font-mono">
              loading…
            </span>
          )}
          <span className="text-xs text-semantic-text-secondary font-mono">
            showing {logs.length.toLocaleString()} of{" "}
            {total.toLocaleString()} events
          </span>
        </div>
      </div>

      {fetchError && (
        <div className="rounded-md border border-semantic-border-primary bg-semantic-bg-danger-secondary text-semantic-text-danger px-3 py-2 text-sm">
          {fetchError}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={total} tone="neutral" />
        <StatCard label="Errors" value={counts.ERROR} tone="error" />
        <StatCard label="Warnings" value={counts.WARN} tone="warning" />
        <StatCard label="Info" value={counts.INFO} tone="success" />
      </div>

      <GenericCard isCollapsible={false} shadow="sm">
        <div className="p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-semantic-text-secondary mb-1">
              Time range
            </label>
            <LogsTimeRangePicker
              value={range}
              mode={rangeMode}
              onChange={handleRangeChange}
              onRefresh={refreshRange}
              isLoading={isFetching}
            />
          </div>
          <div className="flex-1 min-w-[320px]">
            <label className="block text-xs text-semantic-text-secondary mb-1">
              Filters
            </label>
            <LogsFilterBuilder
              nodes={filterNodes}
              onChange={setFilterNodes}
              suggestedKeys={LOG_FILTER_KEYS}
            />
          </div>
          {hasActiveFilter && (
            <div className="pb-1">
              <GenericButton
                label="Clear"
                variant="outlined"
                size="sm"
                onClick={clearFilters}
              />
            </div>
          )}
        </div>
        {filterNodes.length > 1 && (
          <div className="px-4 pb-3 text-[11px] text-semantic-text-secondary font-mono">
            Precedence: <span className="font-semibold">AND</span> binds tighter
            than <span className="font-semibold">OR</span> (SQL-style). Click
            any operator chip to toggle.
          </div>
        )}
      </GenericCard>

      <GenericCard isCollapsible={false} shadow="sm">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-semantic-text-primary uppercase tracking-wide">
              Activity
            </h2>
            <span className="text-xs text-semantic-text-secondary">
              {format(histogramRange.start, "dd MMM HH:mm")} →{" "}
              {format(histogramRange.end, "dd MMM HH:mm")}
            </span>
          </div>
          <LogsHistogram
            buckets={histogram}
            range={histogramRange}
            onBucketClick={(start, end) =>
              handleRangeChange({ start, end }, { kind: "custom" })
            }
          />
        </div>
      </GenericCard>

      <GenericCard isCollapsible={false} shadow="sm" className="flex flex-col">
        <GenericTable
          columns={logColumns}
          data={logs}
          initialRowsPerPage={25}
          rowsPerPageOptions={[25, 50, 100]}
          width="100%"
          tableClassName="bg-semantic-bg-primary"
          maxTableBodyHeight={getTableBodyMaxHeight(540)}
          onRowClick={(row) => setSelectedEntry(row)}
        />
      </GenericCard>

      <LogEntryModal
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />
    </div>
  );
};

export default LogsPage;
